import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Store } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SupplierFormFields, { SUPPLIER_TYPES } from '../components/SupplierFormFields';
import SupplierCard from '../components/SupplierCard';
import SupplierReportDialog from '../components/SupplierReportDialog';

const EMPTY_SUPPLIER = {
  name: '',
  supplier_type: '',
  contact_person: '',
  phone: '',
  email: '',
  address: '',
  account_number: '',
  payment_terms: '',
  notes: '',
};

const SuppliersPage = () => {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierReport, setSupplierReport] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({ ...EMPTY_SUPPLIER });
  const [saving, setSaving] = useState(false);

  const canEdit = user?.access_level !== 'data_entry';
  const canDelete = user?.role === 'main_user' || user?.access_level === 'full_access';

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getSuppliers(filterType !== 'all' ? filterType : null);
      setSuppliers(data);
    } catch {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const resetForm = () => setFormData({ ...EMPTY_SUPPLIER });

  // Optimized field update handler - prevents unnecessary object spreads
  const updateField = (field, value) => {
    setFormData(prev => (prev[field] === value ? prev : { ...prev, [field]: value }));
  };

  const submitSupplier = async (e, isEdit) => {
    e.preventDefault();
    if (!formData.name || !formData.supplier_type) {
      toast.error('Please fill in Supplier Name and Type');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.updateSupplier(selectedSupplier.id, formData);
        toast.success('Supplier updated successfully');
        setShowEditDialog(false);
      } else {
        await api.createSupplier(formData);
        toast.success('Supplier added successfully');
        setShowAddDialog(false);
      }
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'add'} supplier`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteSupplier(selectedSupplier.id);
      toast.success('Supplier deleted successfully');
      setShowDeleteDialog(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  const openEditDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      supplier_type: supplier.supplier_type,
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      account_number: supplier.account_number || '',
      payment_terms: supplier.payment_terms || '',
      notes: supplier.notes || '',
    });
    setShowEditDialog(true);
  };

  const openReportDialog = async (supplier) => {
    setSelectedSupplier(supplier);
    try {
      const report = await api.getSupplierReport(supplier.id);
      setSupplierReport(report);
      setShowReportDialog(true);
    } catch {
      toast.error('Failed to load supplier report');
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Suppliers
          </h1>
          <p className="text-slate-500 mt-1">Manage your fuel stations, parts suppliers, and service providers</p>
        </div>
        <Button onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-supplier-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Supplier
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]" data-testid="filter-type-select">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {SUPPLIER_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{suppliers.length} suppliers</span>
      </div>

      {/* Suppliers Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading suppliers...</div>
      ) : suppliers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="text-center py-12">
            <Store className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No Suppliers Yet</h3>
            <p className="text-slate-400 mb-4">Add your fuel stations, service providers, and parts suppliers</p>
            <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Supplier
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={openEditDialog}
              onDelete={(s) => { setSelectedSupplier(s); setShowDeleteDialog(true); }}
              onReport={openReportDialog}
            />
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Supplier</DialogTitle>
            <DialogDescription>
              Add a fuel station, service provider, or parts supplier
            </DialogDescription>
          </DialogHeader>
          <SupplierFormFields
            isEdit={false}
            formData={formData}
            onFieldChange={updateField}
            onSubmit={(e) => submitSupplier(e, false)}
            onCancel={() => setShowAddDialog(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Supplier</DialogTitle>
            <DialogDescription>Update supplier information</DialogDescription>
          </DialogHeader>
          <SupplierFormFields
            isEdit
            formData={formData}
            onFieldChange={updateField}
            onSubmit={(e) => submitSupplier(e, true)}
            onCancel={() => setShowEditDialog(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedSupplier?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Dialog */}
      <SupplierReportDialog
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        supplier={selectedSupplier}
        report={supplierReport}
      />
    </div>
  );
};

export default SuppliersPage;
